import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import {
  GRID_SIZE,
  INVENTORY_PAGE_COUNT,
  buildPublicImageUrl,
  getSlotIndexFromGridPoint,
  isSlotLocked,
} from './inventoryBagGridUtils';
import { canPlaceItemInGrid } from './inventoryGridBootstrap';
import {
  isSpecialGridDropAllowed,
  resolveSpecialGridDrop,
} from './inventoryGridSpecialDrops';
import { tl } from '../../utils/inlineI18n';

const setNativeDragGhost = (event, imageUrl) => {
  if (!event?.dataTransfer || !imageUrl) return;
  const ghostImage = document.createElement('img');
  ghostImage.src = imageUrl;
  ghostImage.width = 48;
  ghostImage.height = 48;
  ghostImage.style.position = 'absolute';
  ghostImage.style.top = '-1000px';
  ghostImage.style.left = '-1000px';
  ghostImage.style.opacity = '0.9';
  document.body.appendChild(ghostImage);
  event.dataTransfer.setDragImage(ghostImage, 24, 24);
  setTimeout(() => {
    if (ghostImage.parentNode) ghostImage.parentNode.removeChild(ghostImage);
  }, 0);
};

const useInventoryGridDragDrop = ({
  username,
  inventoryGridSlots,
  setInventoryGridSlots,
  unlockedSlotsCount,
  currentInventoryPage,
  setCurrentInventoryPage,
  inventoryGridAreaRef,
  setError,
  errorDropBlockedMessage,
  layoutSaveFailedMessage,
  disabled = false,
  onBeforeGridTouchDrop,
  onSpecialGridDrop,
  onInventoryChanged,
  potStackFullMessage = tl('Bu yığın dolu (en fazla 200 adet).', 'This stack is full (max 200).'),
}) => {
  const [draggedSlotIndex, setDraggedSlotIndex] = useState(null);
  const [draggedInventoryItemKey, setDraggedInventoryItemKey] = useState(null);
  const [dragOverInventoryIndex, setDragOverInventoryIndex] = useState(null);
  const [touchDropTargetIndex, setTouchDropTargetIndex] = useState(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [touchDragGhost, setTouchDragGhost] = useState(null);

  const touchStartPointRef = useRef({ x: 0, y: 0 });
  const touchHasMovedRef = useRef(false);
  const lastTouchTargetIndexRef = useRef(null);
  const touchScrollSnapshotRef = useRef(null);
  const skipNextClickRef = useRef(false);
  const layoutPersistQueueRef = useRef(Promise.resolve());

  const persistGridLayoutInternal = useCallback(async (gridSlots) => {
    if (!username) return;
    const slotsPayload = gridSlots
      .map((slotItem, index) => (
        slotItem && !isSlotLocked(index, unlockedSlotsCount)
          ? { slotIndex: index, itemKey: slotItem.uniqueKey }
          : null
      ))
      .filter(Boolean);

    try {
      await axios.post('/api/inventory/layout', {
        username,
        slots: slotsPayload,
      });
    } catch {
      setError?.(layoutSaveFailedMessage || errorDropBlockedMessage);
    }
  }, [username, unlockedSlotsCount, setError, errorDropBlockedMessage, layoutSaveFailedMessage]);

  const persistGridLayout = useCallback((gridSlots) => {
    layoutPersistQueueRef.current = layoutPersistQueueRef.current
      .then(() => persistGridLayoutInternal(gridSlots))
      .catch(() => {});
    return layoutPersistQueueRef.current;
  }, [persistGridLayoutInternal]);

  const resolveDraggedInventoryIndex = useCallback((event) => {
    if (draggedSlotIndex !== null) return draggedSlotIndex;

    let payloadKey = draggedInventoryItemKey;
    if (!payloadKey && event?.dataTransfer) {
      payloadKey = event.dataTransfer.getData('text/inventory-item-key') || null;
    }
    if (!payloadKey) return null;

    const resolvedIndex = inventoryGridSlots.findIndex(
      (slotItem) => slotItem && slotItem.uniqueKey === payloadKey
    );
    return resolvedIndex >= 0 ? resolvedIndex : null;
  }, [draggedSlotIndex, draggedInventoryItemKey, inventoryGridSlots]);

  const getDraggedGridItem = useCallback((event) => {
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex !== null) return inventoryGridSlots[sourceIndex] || null;
    if (draggedInventoryItemKey) {
      return inventoryGridSlots.find((item) => item?.uniqueKey === draggedInventoryItemKey) || null;
    }
    return null;
  }, [resolveDraggedInventoryIndex, draggedInventoryItemKey, inventoryGridSlots]);

  const isInventoryDropBlocked = useCallback((targetIndex, event) => {
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === null) return true;
    if (sourceIndex === targetIndex) return false;
    if (isSpecialGridDropAllowed({ sourceIndex, targetIndex, gridSlots: inventoryGridSlots })) {
      return false;
    }
    const movingItem = inventoryGridSlots[sourceIndex];
    const result = canPlaceItemInGrid({
      gridSlots: inventoryGridSlots,
      sourceIndex,
      targetIndex,
      movingItem,
      unlockedCount: unlockedSlotsCount,
    });
    return !result.ok;
  }, [resolveDraggedInventoryIndex, inventoryGridSlots, unlockedSlotsCount]);

  const tryApplySpecialGridDrop = useCallback(async (sourceIndex, targetIndex) => {
    const specialDrop = resolveSpecialGridDrop({
      sourceIndex,
      targetIndex,
      gridSlots: inventoryGridSlots,
    });
    if (specialDrop?.type === 'mergePots') {
      if (specialDrop.targetFull) {
        setError?.(potStackFullMessage);
        return true;
      }
      try {
        const res = await axios.post('/api/inventory/merge-pots', {
          username,
          sourceId: specialDrop.sourceId,
          targetId: specialDrop.targetId,
        });
        if (!res.data?.success) setError?.(res.data?.message || potStackFullMessage);
      } catch (err) {
        setError?.(err.response?.data?.message || err.message);
      }
      await onInventoryChanged?.();
      return true;
    }
    if (!specialDrop || !onSpecialGridDrop) return false;
    await onSpecialGridDrop(specialDrop);
    return true;
  }, [inventoryGridSlots, onSpecialGridDrop, onInventoryChanged, potStackFullMessage, setError, username]);

  const clearDragState = useCallback(() => {
    setDragOverInventoryIndex(null);
    setTouchDropTargetIndex(null);
    setDraggedSlotIndex(null);
    setDraggedInventoryItemKey(null);
    setIsTouchDragging(false);
    setTouchDragGhost(null);
    touchHasMovedRef.current = false;
    lastTouchTargetIndexRef.current = null;
  }, []);

  const getSlotIndexFromPoint = useCallback((x, y) => {
    const elementAtPoint = document.elementFromPoint(x, y);
    const slotElement = elementAtPoint?.closest?.('[data-slot-index]');
    if (slotElement) {
      const slotIndex = Number(slotElement.dataset.slotIndex);
      if (Number.isInteger(slotIndex) && slotIndex >= 0 && slotIndex < GRID_SIZE) {
        return slotIndex;
      }
    }
    return getSlotIndexFromGridPoint(x, y, inventoryGridAreaRef.current, currentInventoryPage);
  }, [inventoryGridAreaRef, currentInventoryPage]);

  const getInventoryPageFromPoint = useCallback((x, y) => {
    const elementAtPoint = document.elementFromPoint(x, y);
    const pageElement = elementAtPoint?.closest?.('[data-inventory-page]');
    if (!pageElement) return null;
    const page = Number(pageElement.dataset.inventoryPage);
    if (!Number.isInteger(page) || page < 1 || page > INVENTORY_PAGE_COUNT) return null;
    return page;
  }, []);

  const lockTouchDragScroll = () => {
    if (touchScrollSnapshotRef.current) return;
    touchScrollSnapshotRef.current = {
      bodyOverflow: document.body.style.overflow,
      bodyTouchAction: document.body.style.touchAction,
      bodyOverscroll: document.body.style.overscrollBehavior,
      htmlOverflow: document.documentElement.style.overflow,
      htmlTouchAction: document.documentElement.style.touchAction,
      htmlOverscroll: document.documentElement.style.overscrollBehavior,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.touchAction = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
  };

  const unlockTouchDragScroll = () => {
    const snapshot = touchScrollSnapshotRef.current;
    if (!snapshot) return;
    document.body.style.overflow = snapshot.bodyOverflow;
    document.body.style.touchAction = snapshot.bodyTouchAction;
    document.body.style.overscrollBehavior = snapshot.bodyOverscroll;
    document.documentElement.style.overflow = snapshot.htmlOverflow;
    document.documentElement.style.touchAction = snapshot.htmlTouchAction;
    document.documentElement.style.overscrollBehavior = snapshot.htmlOverscroll;
    touchScrollSnapshotRef.current = null;
  };

  const handleGridDragStart = useCallback((event, index, slotItem) => {
    if (disabled || !slotItem) {
      event.preventDefault();
      return;
    }
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/inventory-item-key', slotItem.uniqueKey);
      setNativeDragGhost(event, buildPublicImageUrl(slotItem.baseItem?.imageUrl));
    }
    setDraggedSlotIndex(index);
    setDraggedInventoryItemKey(slotItem.uniqueKey);
  }, [disabled]);

  const handleGridDragEnd = useCallback(() => {
    clearDragState();
  }, [clearDragState]);

  const handleInventorySlotDragOver = useCallback((event, index) => {
    if (disabled || (draggedSlotIndex === null && !draggedInventoryItemKey)) return;
    event.preventDefault();
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === index) {
      setDragOverInventoryIndex(null);
      return;
    }
    if (event.dataTransfer) {
      const blocked = isInventoryDropBlocked(index, event);
      event.dataTransfer.dropEffect = blocked ? 'none' : 'move';
    }
    setDragOverInventoryIndex(index);
  }, [disabled, draggedSlotIndex, draggedInventoryItemKey, resolveDraggedInventoryIndex, isInventoryDropBlocked]);

  const handleInventorySlotDragLeave = useCallback(() => {
    setDragOverInventoryIndex(null);
  }, []);

  const handleGridDrop = useCallback(async (event, targetIndex) => {
    if (disabled) return;
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === null || sourceIndex === targetIndex) {
      clearDragState();
      return;
    }

    const specialHandled = await tryApplySpecialGridDrop(sourceIndex, targetIndex);
    if (specialHandled) {
      clearDragState();
      return;
    }

    if (isInventoryDropBlocked(targetIndex, event)) {
      setError?.(errorDropBlockedMessage);
      clearDragState();
      return;
    }

    const next = [...inventoryGridSlots];
    const draggedItem = next[sourceIndex];
    next[sourceIndex] = null;
    next[targetIndex] = draggedItem;
    setInventoryGridSlots(next);
    persistGridLayout(next);
    clearDragState();
  }, [
    disabled,
    resolveDraggedInventoryIndex,
    tryApplySpecialGridDrop,
    isInventoryDropBlocked,
    inventoryGridSlots,
    setInventoryGridSlots,
    persistGridLayout,
    setError,
    errorDropBlockedMessage,
    clearDragState,
  ]);

  const handleInventoryTabDragOver = useCallback((event, page) => {
    if (disabled || !isTouchDragging) return;
    if (draggedSlotIndex === null && !draggedInventoryItemKey) return;
    event.preventDefault();
    if (page !== currentInventoryPage) {
      setCurrentInventoryPage(page);
      setDragOverInventoryIndex(null);
      setTouchDropTargetIndex(null);
    }
  }, [disabled, isTouchDragging, draggedSlotIndex, draggedInventoryItemKey, currentInventoryPage, setCurrentInventoryPage]);

  const handleGridTouchStart = useCallback((event, index, slotItem) => {
    if (disabled || !slotItem) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    lockTouchDragScroll();
    touchStartPointRef.current = { x: touch.clientX, y: touch.clientY };
    touchHasMovedRef.current = false;
    lastTouchTargetIndexRef.current = null;
    setTouchDropTargetIndex(null);
    setIsTouchDragging(true);
    setTouchDragGhost({
      imageUrl: buildPublicImageUrl(slotItem.baseItem?.imageUrl),
      x: touch.clientX,
      y: touch.clientY,
    });
    setDraggedSlotIndex(index);
    setDraggedInventoryItemKey(slotItem.uniqueKey);
  }, [disabled]);

  const handleGridTouchMove = useCallback((event) => {
    if (draggedSlotIndex === null && !draggedInventoryItemKey) return;

    const touch = event.touches?.[0];
    if (!touch) return;
    setTouchDragGhost((prev) => (prev ? { ...prev, x: touch.clientX, y: touch.clientY } : prev));

    const targetPage = getInventoryPageFromPoint(touch.clientX, touch.clientY);
    if (targetPage && targetPage !== currentInventoryPage) {
      touchHasMovedRef.current = true;
      setCurrentInventoryPage(targetPage);
      setTouchDropTargetIndex(null);
      setDragOverInventoryIndex(null);
      requestAnimationFrame(() => {
        const refreshedTargetIndex = getSlotIndexFromPoint(touch.clientX, touch.clientY);
        const canUseTargetIndex = refreshedTargetIndex !== null
          && refreshedTargetIndex !== draggedSlotIndex;
        if (canUseTargetIndex) {
          setTouchDropTargetIndex(refreshedTargetIndex);
          setDragOverInventoryIndex(refreshedTargetIndex);
          lastTouchTargetIndexRef.current = refreshedTargetIndex;
        }
      });
    }

    const dx = Math.abs(touch.clientX - touchStartPointRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartPointRef.current.y);
    if (dx > 6 || dy > 6) {
      touchHasMovedRef.current = true;
    }

    const targetIndex = getSlotIndexFromPoint(touch.clientX, touch.clientY);
    const canUseTargetIndex = targetIndex !== null && targetIndex !== draggedSlotIndex;
    setTouchDropTargetIndex(canUseTargetIndex ? targetIndex : null);
    setDragOverInventoryIndex(canUseTargetIndex ? targetIndex : null);
    if (canUseTargetIndex) {
      lastTouchTargetIndexRef.current = targetIndex;
    }
  }, [
    draggedSlotIndex,
    draggedInventoryItemKey,
    currentInventoryPage,
    setCurrentInventoryPage,
    getInventoryPageFromPoint,
    getSlotIndexFromPoint,
  ]);

  const handleGridTouchEnd = useCallback(async (event) => {
    if (draggedSlotIndex === null && !draggedInventoryItemKey) return;

    const changedTouch = event.changedTouches?.[0];
    if (touchHasMovedRef.current && changedTouch) {
      const draggedItem = draggedSlotIndex !== null ? inventoryGridSlots[draggedSlotIndex] : null;
      const handledExternally = await onBeforeGridTouchDrop?.({
        x: changedTouch.clientX,
        y: changedTouch.clientY,
        draggedItem,
        sourceIndex: draggedSlotIndex,
      });
      if (!handledExternally) {
        const targetIndexFromPoint = getSlotIndexFromPoint(changedTouch.clientX, changedTouch.clientY);
        const targetIndex = targetIndexFromPoint
          ?? touchDropTargetIndex
          ?? dragOverInventoryIndex
          ?? lastTouchTargetIndexRef.current;
        if (targetIndex !== null && draggedSlotIndex !== null && targetIndex !== draggedSlotIndex) {
          const specialHandled = await tryApplySpecialGridDrop(draggedSlotIndex, targetIndex);
          if (specialHandled) {
            skipNextClickRef.current = true;
          } else if (isInventoryDropBlocked(targetIndex)) {
            setError?.(errorDropBlockedMessage);
          } else {
            const next = [...inventoryGridSlots];
            const item = next[draggedSlotIndex];
            next[draggedSlotIndex] = null;
            next[targetIndex] = item;
            setInventoryGridSlots(next);
            persistGridLayout(next);
            skipNextClickRef.current = true;
          }
        }
      } else {
        skipNextClickRef.current = true;
      }
    }

    unlockTouchDragScroll();
    clearDragState();
  }, [
    draggedSlotIndex,
    draggedInventoryItemKey,
    inventoryGridSlots,
    onBeforeGridTouchDrop,
    getSlotIndexFromPoint,
    touchDropTargetIndex,
    dragOverInventoryIndex,
    tryApplySpecialGridDrop,
    isInventoryDropBlocked,
    setInventoryGridSlots,
    persistGridLayout,
    setError,
    errorDropBlockedMessage,
    clearDragState,
  ]);

  const handleGridTouchCancel = useCallback(() => {
    unlockTouchDragScroll();
    clearDragState();
  }, [clearDragState]);

  return {
    draggedSlotIndex,
    draggedInventoryItemKey,
    dragOverInventoryIndex,
    touchDropTargetIndex,
    isTouchDragging,
    touchDragGhost,
    skipNextClickRef,
    getDraggedGridItem,
    isGridDropBlocked: (targetIndex) => isInventoryDropBlocked(targetIndex, null),
    handleGridDragStart,
    handleGridDragEnd,
    handleInventorySlotDragOver,
    handleInventorySlotDragLeave,
    handleGridDrop,
    handleInventoryTabDragOver,
    handleGridTouchStart,
    handleGridTouchMove,
    handleGridTouchEnd,
    handleGridTouchCancel,
  };
};

export default useInventoryGridDragDrop;
